import { useEffect, useState, useCallback } from "react";
import { http, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { LogOut, Users, LogIn, LogOut as LogOutIco, UtensilsCrossed, Flame, IndianRupee, AlertCircle, CalendarDays, Wallet, BellRing, BedDouble, ExternalLink } from "lucide-react";
import MenuManager from "@/pages/admin/MenuManager";
import ServicesManager from "@/pages/admin/ServicesManager";
import AttractionsManager from "@/pages/admin/AttractionsManager";
import BookingManager from "@/pages/admin/BookingManager";
import BookingCalendar from "@/pages/admin/BookingCalendar";
import RoomsManager from "@/pages/admin/RoomsManager";
import GalleryManager from "@/pages/admin/GalleryManager";
import StaffManager from "@/pages/admin/StaffManager";
import AmenitiesManager from "@/pages/admin/AmenitiesManager";
import FAQManager from "@/pages/admin/FAQManager";
import OverviewTab from "@/pages/admin/dashboard/OverviewTab";
import SettingsTab from "@/pages/admin/dashboard/SettingsTab";
import { StaysTable, GuestsTable, OrdersTable, ServicesTable, BillsTable } from "@/pages/admin/dashboard/tables";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stays, setStays] = useState([]);
  const [guests, setGuests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [prop, setProp] = useState(null);
  const [reviewCfg, setReviewCfg] = useState(null);
  const [brandCfg, setBrandCfg] = useState(null);
  const [nbKey, setNbKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await http.get("/admin/dashboard");
      setD(r.data);
    } catch { nav("/login"); }
  }, [nav]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const fetchStays = useCallback(() => { http.get("/admin/stays").then((r) => setStays(r.data.stays)).catch(() => {}); }, []);
  const fetchOrders = useCallback(() => { http.get("/admin/orders").then((r) => setOrders(r.data.orders)).catch(() => {}); }, []);
  const fetchServices = useCallback(() => { http.get("/admin/service-requests").then((r) => setReqs(r.data.requests)).catch(() => {}); }, []);
  const fetchGuests = useCallback(() => { http.get("/admin/guests").then((r) => setGuests(r.data.guests)).catch(() => {}); }, []);

  useEffect(() => {
    if (tab === "stays" || tab === "bills") fetchStays();
    if (tab === "guests") fetchGuests();
    if (tab === "orders") fetchOrders();
    if (tab === "services") fetchServices();
    if (tab === "staff") http.get("/admin/staff").then((r) => setStaffList(r.data.staff));
    if (tab === "settings") {
      http.get("/public/property").then((r) => setProp(r.data.property));
      http.get("/admin/settings/google-review").then((r) => setReviewCfg(r.data)).catch(() => {});
      http.get("/public/branding").then((r) => setBrandCfg(r.data.logo_url)).catch(() => {});
    }
  }, [tab, fetchStays, fetchOrders, fetchServices, fetchGuests]);

  const doLogout = async () => { await logout(); nav("/login"); };
  if (!d) return null;

  return (
    <div className="min-h-screen" style={{ background: "var(--surface)" }}>
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <BrandLogo className="h-10 mb-2" />
            <h1 className="font-serif text-4xl">Owner Dashboard</h1>
            <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>{user?.name} · Managing remotely from anywhere</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="admin-new-booking" onClick={() => { setTab("bookings"); setNbKey((k) => k + 1); }} className="btn-primary text-sm">+ New Direct Booking</button>
            <Link to="/kitchen" data-testid="admin-open-kitchen" className="btn-outline text-sm">Kitchen View</Link>
            <Link to="/staff" data-testid="admin-open-staff" className="btn-outline text-sm">Staff View</Link>
            <button data-testid="admin-logout" onClick={doLogout} className="btn-outline text-sm"><LogOut size={14} className="inline mr-1" />Sign Out</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          <Stat icon={Users} label="Current guests" value={d.current_guests.length} testid="admin-stat-current" />
          <Stat icon={LogIn} label="Check-ins today" value={d.checkins_today} testid="admin-stat-checkins" />
          <Stat icon={LogOutIco} label="Check-outs today" value={d.checkouts_today} testid="admin-stat-checkouts" />
          <Stat icon={UtensilsCrossed} label="Pending orders" value={d.pending_orders} testid="admin-stat-pending-orders" />
          <Stat icon={Flame} label="Pending services" value={d.pending_services} testid="admin-stat-pending-services" />
          <Stat icon={IndianRupee} label="Today's revenue" value={inr(d.today_revenue)} testid="admin-stat-revenue" />
          <Stat icon={AlertCircle} label="Outstanding" value={inr(d.outstanding_balance)} testid="admin-stat-outstanding" accent />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <Stat icon={CalendarDays} label="Upcoming bookings" value={d.upcoming_bookings} testid="admin-stat-upcoming" />
          <Stat icon={Wallet} label="Payments to verify" value={d.payments_pending} testid="admin-stat-payverify" accent={d.payments_pending > 0} />
          <Stat icon={BellRing} label="Unacknowledged" value={d.unacknowledged} testid="admin-stat-unack" accent={d.unacknowledged > 0} />
          <Stat icon={BedDouble} label="Rooms available today" value={`${d.rooms_available}/${d.rooms_total}`} testid="admin-stat-rooms" />
          <Stat icon={ExternalLink} label="OTA action required" value={d.external_action_required} testid="admin-stat-ota" accent={d.external_action_required > 0} />
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto">
          {Object.entries({
            overview: "Dashboard", bookings: "Bookings", calendar: "Calendar", stays: "Stays", guests: "Guests",
            orders: "Food Orders", services: "Service Requests", menu: "Food Menu", pricing: "Services & Pricing",
            bills: "Bills & Payments", staff: "Staff", explore: "Website Content", rooms: "Rooms", photos: "Photos", amenities: "Amenities", faqs: "FAQs", settings: "Settings",
          }).map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)} className="text-sm px-4 py-2 rounded-full whitespace-nowrap"
              style={{ background: tab === key ? "var(--terracotta)" : "var(--card)", color: tab === key ? "#fff" : "var(--fg)", border: "1px solid var(--border)" }}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab d={d} />}

        {tab === "stays" && <StaysTable stays={stays} onChanged={fetchStays} />}

        {tab === "guests" && <GuestsTable guests={guests} onChanged={fetchGuests} />}

        {tab === "orders" && <OrdersTable orders={orders} onChanged={fetchOrders} />}

        {tab === "menu" && <MenuManager />}

        {tab === "pricing" && <ServicesManager />}

        {tab === "explore" && <AttractionsManager />}

        {tab === "bookings" && <BookingManager triggerNew={nbKey} />}

        {tab === "calendar" && <BookingCalendar />}

        {tab === "bills" && <BillsTable stays={stays} onChanged={fetchStays} />}

        {tab === "staff" && <StaffManager />}

        {tab === "rooms" && <RoomsManager />}

        {tab === "photos" && <GalleryManager />}

        {tab === "amenities" && <AmenitiesManager />}

        {tab === "faqs" && <FAQManager />}

        {tab === "settings" && prop && (
          <SettingsTab prop={prop} reviewCfg={reviewCfg} setReviewCfg={setReviewCfg} brandCfg={brandCfg} setBrandCfg={setBrandCfg} />
        )}

        {tab === "services" && <ServicesTable reqs={reqs} onChanged={fetchServices} />}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className="card" style={{ padding: 16 }} data-testid={testid}>
      <Icon size={18} style={{ color: accent ? "var(--terracotta)" : "var(--pine)" }} />
      <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{label}</div>
      <div className="font-serif text-2xl" style={{ color: accent ? "var(--terracotta)" : "var(--fg)" }}>{value}</div>
    </div>
  );
}
